/**
 * TodaysReadingWidget
 * -----------------------------------------------------------------------------
 * A shared "Today's Reading" tile that renders the current user's active
 * reading plan (or a featured plan preview if there's none / no login).
 *
 * Consumes `GET /api/bible/reading/today[?church_id=]` — always returns a
 * usable payload so this widget never blanks.
 *
 * Usage:
 *   <TodaysReadingWidget />                       // any signed-in user
 *   <TodaysReadingWidget churchId={church.id} />  // church-context reader
 *   <TodaysReadingWidget compact />               // narrow dashboard tile
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Book, BookOpen, Flame, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;

const sourceLabel = {
  user_plan: 'Your plan',
  church: 'Church plan',
  featured: 'Featured plan',
};

const TodaysReadingWidget = ({
  churchId = null,
  compact = false,
  className = '',
  showCta = true,
}) => {
  const token = getToken();
  const { data, isLoading } = useQuery({
    queryKey: ['bible-reading-today', churchId || 'user'],
    queryFn: async () => {
      const qs = churchId ? `?church_id=${encodeURIComponent(churchId)}` : '';
      const res = await fetch(`${API}/api/bible/reading/today${qs}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 5 * 60 * 1000, // 5 min
  });

  if (isLoading) {
    return (
      <Card className={className} data-testid="todays-reading-loading">
        <CardContent className="p-4">
          <div className="h-4 w-24 bg-slate-200 animate-pulse rounded mb-2" />
          <div className="h-6 w-48 bg-slate-200 animate-pulse rounded mb-3" />
          <div className="h-3 w-full bg-slate-200 animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }
  if (!data || !data.plan_title) {
    return (
      <Card className={className} data-testid="todays-reading-empty">
        <CardContent className="p-4 text-sm text-slate-500 flex items-center gap-2">
          <Book className="w-4 h-4 text-slate-400" />
          No reading plan available. Add one to start your journey.
        </CardContent>
      </Card>
    );
  }

  const source = data.source;
  const streak = Number(data.streak || 0);
  const completed = !!data.completed_today;
  const dayLabel = `Day ${data.current_day}${data.duration_days ? ` of ${data.duration_days}` : ''}`;
  const passages = Array.isArray(data.passages) ? data.passages : [];

  return (
    <Card
      className={`overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-white ${className}`}
      data-testid="todays-reading-widget"
    >
      <CardContent className={compact ? 'p-3' : 'p-5'}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[11px] uppercase tracking-wide font-semibold text-indigo-700">
                Today&apos;s Reading
              </span>
              {source && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid={`todays-reading-source-${source}`}>
                  {source === 'featured' && <Sparkles className="w-2.5 h-2.5 mr-0.5" />}
                  {sourceLabel[source] || source}
                </Badge>
              )}
              {completed && (
                <Badge className="bg-green-500 hover:bg-green-500 text-white text-[10px] px-1.5 py-0" data-testid="todays-reading-completed">
                  <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                  Done
                </Badge>
              )}
            </div>
            <div className={`font-semibold truncate ${compact ? 'text-sm' : 'text-base'}`} data-testid="todays-reading-title">
              {data.day_title || data.plan_title}
            </div>
            <div className="text-xs text-slate-500 truncate">
              {data.plan_title} · {dayLabel}
            </div>
            {!!passages.length && (
              <div className="mt-2 flex flex-wrap gap-1">
                {passages.slice(0, 3).map((p, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0" data-testid={`todays-reading-passage-${i}`}>
                    {p}
                  </Badge>
                ))}
                {passages.length > 3 && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">+{passages.length - 3}</Badge>
                )}
              </div>
            )}
            {!compact && data.devotional_text && (
              <p className="text-xs text-slate-600 mt-2 line-clamp-2">
                {data.devotional_text}
              </p>
            )}
          </div>
          {streak > 0 && (
            <div className="text-right flex-shrink-0" title={`${streak}-day streak`}>
              <div className="flex items-center gap-0.5 text-amber-600">
                <Flame className="w-3.5 h-3.5" />
                <span className="text-sm font-semibold">{streak}</span>
              </div>
              <div className="text-[10px] text-slate-500">day streak</div>
            </div>
          )}
        </div>

        {showCta && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
            {data.duration_days ? (
              <div className="flex items-center gap-2 flex-1">
                <div className="h-1.5 bg-slate-100 rounded-full flex-1 overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 transition-all"
                    style={{ width: `${Math.max(2, Math.min(100, data.completion_percent || 0))}%` }}
                  />
                </div>
                <span className="text-[11px] text-slate-500 tabular-nums">
                  {Math.round(data.completion_percent || 0)}%
                </span>
              </div>
            ) : <span />}
            <Button
              asChild
              size="sm"
              variant="ghost"
              className="ml-2 h-7 text-xs text-indigo-700 hover:bg-indigo-100"
              data-testid="todays-reading-cta"
            >
              <Link to={source === 'user_plan' ? `/bible/plan/${data.user_plan_id || data.plan_id}` : `/bible/plans/${data.plan_id}`}>
                Read <ArrowRight className="w-3 h-3 ml-1" />
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TodaysReadingWidget;
